/*
 * Consent management with Klaro
 * 
 * Implementation consistent with Google Consent Mode v2
 *
 * Although the official Klaro tutorial references GTM (https://klaro.org/docs/tutorials/google_tag_manager),
 * this implementation integrates directly with GA4 without GTM, which has the same Google Consent Mode v2 compliance result.
 *
 * How it works:
 * - GA4 loads on every page visit (even before the user decides), with all signals denied by default.
 * - When the user accepts analytics in the Klaro banner, only `analytics_storage` is granted.
 * - Ad signals (ad_storage, ad_user_data, ad_personalization) are always kept denied since this site uses analytics only, not advertising.
 *
 * Testing with Tag Assistant (Chrome extension):
 * - Note: Tag Assistant does NOT update live — close and reopen it after each consent change to see the current state.
 * - Look for the "Cookie Consent State" parameter. After the user accepts analytics, it should show:
 *     G100 — analytics denied, ads denied (default, appears after user denies Klaro settings OR before user accepts or denies Klaro settings)
 *     G101 — means analytics granted, ads denied (appears after user accepts Klaro settings)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '@hooks/useData';
import { useTranslation } from 'react-i18next';
import {
    buildCookieConsentConfig,
    buildPolicyUrl,
    COOKIE_CONSENT_STORAGE_NAME,
    COOKIE_CONSENT_ELEMENT_ID,
    KlaroConfig,
} from '@services/cookieConsent';

declare global {
    interface Window {
        klaro?: {
            setup: (config: KlaroConfig) => void;
            show: (config?: KlaroConfig) => void;
            getManager: (config?: KlaroConfig) => {
                confirmed: boolean;
                watch: (watcher: KlaroWatcher) => void;
                unwatch: (watcher: KlaroWatcher) => void;
            };
        };
        gtag?: (...args: unknown[]) => void;
        dataLayer?: unknown[];
    }
}

type KlaroWatcher = {
    update: (manager: { confirmed: boolean }, eventName: string) => void;
};

const DEFAULT_LANGUAGES = ['pt', 'en', 'fr', 'es', 'de'];

const hasStoredDecision = () => {
    try {
        return Boolean(localStorage.getItem(COOKIE_CONSENT_STORAGE_NAME));
    } catch {
        return false;
    }
};

const getStoredAnalyticsConsent = (): boolean => {
    try {
        const stored = localStorage.getItem(COOKIE_CONSENT_STORAGE_NAME);
        if (!stored) return false;
        const parsed = JSON.parse(stored);
        return !!parsed?.['analytics-suite'];
    } catch {
        return false;
    }
};

const initGtagDataLayer = () => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = function () {
            // eslint-disable-next-line prefer-rest-params
            (window.dataLayer as unknown[]).push(arguments);
        };
    }
};

export const useCookieConsent = () => {
    const { allLanguages, allSettings } = useData();
    const { t, i18n } = useTranslation();
    const [hasDecision, setHasDecision] = useState<boolean>(() => hasStoredDecision());
    const [isReady, setIsReady] = useState<boolean>(false);
    const configRef = useRef<KlaroConfig | null>(null);
    const watcherRef = useRef<KlaroWatcher | null>(null);
    const initialPromptShownRef = useRef<boolean>(false);
    const gaInitializedRef = useRef<boolean>(false);
    const currentGaIdRef = useRef<string | null>(null);

    const fallbackLanguage = useMemo(() => {
        const apiDefault = allLanguages?.data?.find((language) => language.default === 1)?.language_code;
        return apiDefault || 'pt';
    }, [allLanguages]);

    const hasAnalyticsConfig = useMemo(() => {
        const analyticsSetting = (allSettings?.data as Record<string, unknown> | undefined)?.['google_analytics'];
        if (typeof analyticsSetting === 'string') {
            return analyticsSetting.trim().length > 0;
        }
        return Boolean(analyticsSetting);
    }, [allSettings]);

    const languages = useMemo(() => {
        const apiLanguages =
            allLanguages?.data?.map((language) => language.language_code).filter((code): code is string => Boolean(code)) || [];
        return Array.from(new Set([...DEFAULT_LANGUAGES, ...apiLanguages]));
    }, [allLanguages]);

    const analyticsMeasurementId = useMemo(() => {
        const setting = (allSettings?.data as Record<string, unknown> | undefined)?.['google_analytics'];
        if (typeof setting === 'string') {
            const trimmed = setting.trim();
            return trimmed.length > 0 ? trimmed : null;
        }
        if (setting && typeof setting === 'object') {
            const maybeId = (setting as Record<string, unknown>)['id'] || (setting as Record<string, unknown>)['measurementId'];
            if (typeof maybeId === 'string' && maybeId.trim().length > 0) return maybeId.trim();
        }
        return null;
    }, [allSettings]);

    const resolvePolicyUrl = useCallback((language?: string) => buildPolicyUrl(language, fallbackLanguage), [fallbackLanguage]);

    const cookieConfig = useMemo(() => {
        if (!hasAnalyticsConfig) {
            return null;
        }
        return buildCookieConsentConfig({
            languages,
            fallbackLanguage,
            currentLanguage: i18n.language,
            getTranslator: i18n.getFixedT.bind(i18n),
            onPreferenceChange: (service, consent) => {
                window.dispatchEvent(new CustomEvent('cookie-preferences-change', { detail: { service, consent } }));
            },
            policyUrlResolver: resolvePolicyUrl,
        });
    }, [languages, fallbackLanguage, i18n, resolvePolicyUrl, hasAnalyticsConfig]);

    const detachWatcher = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !watcherRef.current || !configRef.current) {
            return;
        }
        const manager = window.klaro.getManager(configRef.current);
        manager?.unwatch(watcherRef.current);
        watcherRef.current = null;
    }, [hasAnalyticsConfig]);

    const attachWatcher = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !configRef.current) {
            return;
        }
        const manager = window.klaro.getManager(configRef.current);
        if (!manager) {
            return;
        }
        detachWatcher();
        const watcher: KlaroWatcher = {
            update: (_, eventName) => {
                if (eventName === 'saveConsents') {
                    setHasDecision(true);
                }
            },
        };
        watcherRef.current = watcher;
        manager.watch(watcher);
        setHasDecision(manager.confirmed || hasStoredDecision());
    }, [detachWatcher, hasAnalyticsConfig]);

    useEffect(() => {
        configRef.current = cookieConfig;
    }, [cookieConfig]);

    useEffect(() => {
        let cancelled = false;

        const loadKlaro = async () => {
            if (typeof window === 'undefined' || !cookieConfig || !hasAnalyticsConfig) return;

            if (!window.klaro) {
                const klaroModule = await import('klaro/dist/klaro-no-translations.js');
                const klaroInstance = (klaroModule && (klaroModule.default || klaroModule)) as Window['klaro'];
                if (klaroInstance) {
                    window.klaro = klaroInstance;
                }
            }
            if (cancelled || !window.klaro || !configRef.current) {
                return;
            }

            const alreadyDecided = hasStoredDecision();
            window.klaro.setup(configRef.current);
            attachWatcher();
            setIsReady(true);
            setHasDecision((prev) => prev || alreadyDecided);

            if (!alreadyDecided && !initialPromptShownRef.current) {
                initialPromptShownRef.current = true;
                window.klaro.show(configRef.current);
            }
        };

        if (hasAnalyticsConfig) {
            loadKlaro();
        }
        return () => {
            cancelled = true;
            detachWatcher();
        };
    }, [cookieConfig, attachWatcher, detachWatcher, hasAnalyticsConfig]);

    // Load GA4 with Consent Mode v2 defaults (denied) — runs regardless of user decision
    const loadGA4 = useCallback((id: string) => {
        if (typeof window === 'undefined' || !id) return;
        if (gaInitializedRef.current && currentGaIdRef.current === id) return;

        // Must happen before gtag.js loads so GA4 reads the correct defaults from dataLayer
        initGtagDataLayer();
        window.gtag?.('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            wait_for_update: 500,
        });

        if (!document.getElementById('ga4-gtag-src')) {
            const script = document.createElement('script');
            script.id = 'ga4-gtag-src';
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
            document.head.appendChild(script);
        }

        window.gtag?.('js', new Date());

        // Update consent before config so the page_view hit carries the correct state
        if (getStoredAnalyticsConsent()) {
            window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
        }

        window.gtag?.('config', id, { anonymize_ip: true });

        gaInitializedRef.current = true;
        currentGaIdRef.current = id;
    }, []);

    const updateConsentSignal = useCallback((granted: boolean) => {
        window.gtag?.('consent', 'update', {
            analytics_storage: granted ? 'granted' : 'denied',
        });
    }, []);

    // Initialize GA4 as soon as the measurement ID is available
    useEffect(() => {
        if (!analyticsMeasurementId) return;
        loadGA4(analyticsMeasurementId);
    }, [analyticsMeasurementId, loadGA4]);

    // Update consent signals when the user makes a decision in Klaro
    useEffect(() => {
        if (!hasAnalyticsConfig || !analyticsMeasurementId) return;

        const handleConsentEvent = (e: Event) => {
            const evt = e as CustomEvent<{ service: string; consent: boolean }>;
            if (!evt.detail) return;
            const { service, consent } = evt.detail;
            if (service !== 'analytics') return;
            updateConsentSignal(consent);
        };

        window.addEventListener('cookie-preferences-change', handleConsentEvent as EventListener);
        return () => {
            window.removeEventListener('cookie-preferences-change', handleConsentEvent as EventListener);
        };
    }, [hasAnalyticsConfig, analyticsMeasurementId, updateConsentSignal]);

    // Ensure privacy policy links open in the same tab (avoid blank page/new tab)
    useEffect(() => {
        if (!hasAnalyticsConfig) return;

        const policyUrls = new Set<string>();
        try {
            const all = Array.from(new Set([fallbackLanguage, ...languages]));
            all.forEach((lang) => {
                // Use undefined for default language resolution to mirror Klaro's default mapping
                const isDefault = lang === fallbackLanguage;
                const rel = resolvePolicyUrl(isDefault ? undefined : lang);
                const abs = new URL(rel, window.location.origin).href;
                policyUrls.add(abs);
            });
        } catch {
            // ignore URL resolution issues
        }

        const onClick = (e: MouseEvent) => {
            const target = e.target as Element | null;
            if (!target) return;
            const anchor = (target.closest ? target.closest('a[href]') : null) as HTMLAnchorElement | null;
            if (!anchor || !anchor.href) return;

            // Limit to clicks inside Klaro element, if present
            const container = anchor.closest(`#${COOKIE_CONSENT_ELEMENT_ID}`);
            if (!container) return;

            if (policyUrls.has(anchor.href)) {
                e.preventDefault();
                // Force same-tab navigation
                window.location.assign(anchor.href);
            }
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnalyticsConfig, languages, fallbackLanguage, resolvePolicyUrl]);

    const openManager = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !configRef.current) return;
        window.klaro.show(configRef.current);
    }, [hasAnalyticsConfig]);

    return {
        manageButtonLabel: t('cookie-manager.manage-button'),
        openManager,
        shouldDisplayTrigger: hasAnalyticsConfig && isReady && hasDecision,
        shouldRender: hasAnalyticsConfig,
    };
};
